/**
 * Connections: an object's events wired to things other objects do, like
 * Godot's signals. They are saved on the object whose event fires:
 *
 *   "connections": [
 *     { "on": "enter", "who": ["Crate", "Player"], "to": "o4c1", "do": "clip", "args": ["open"] },
 *     { "on": "enter", "to": "@camera", "do": "shake", "args": [200] },
 *     { "on": "message", "name": "boss_down", "do": "show" }
 *   ]
 *
 * `to` is an object id in the same file (so a connection survives renames and
 * travels with its prefab), `@camera` for the scene's camera, or empty for the
 * object itself. `do` is a built-in action or the name of a method a script
 * on the target has.
 */

export interface Connection {
  /** The event: a built-in one (see CONNECTION_EVENTS), or one a script or plugin fires on the object. */
  on: string;
  /** For `enter` and `leave`: the names of the objects that count. Empty: any object with a physics body. */
  who?: string[];
  /** For `message`, `clipDone` and `animDone`: which message, clip or animation. Empty: any. */
  name?: string;
  /** The object it acts on: an id in the same file, or `@camera`. Empty: this object. */
  to?: string;
  /** A built-in action (see CONNECTION_ACTIONS) or a method of a script on the target. */
  do: string;
  args?: unknown[];
}

/** The scene's camera, as a connection target. */
export const CAMERA_TARGET = '@camera';

export interface ConnectionEventInfo {
  label: string;
  /** What the event's `name` picks, when it takes one. */
  name?: 'message' | 'clip' | 'animation';
  /** Takes `who`. */
  who?: boolean;
}

/** Events every object has. Scripts and plugins can add their own. */
export const CONNECTION_EVENTS: Record<string, ConnectionEventInfo> = {
  click: { label: 'clicked' },
  over: { label: 'pointer moves over it' },
  out: { label: 'pointer leaves it' },
  enter: { label: 'entered by', who: true },
  leave: { label: 'left by', who: true },
  clipDone: { label: 'a clip finishes', name: 'clip' },
  animDone: { label: 'an animation finishes', name: 'animation' },
  message: { label: 'a message is sent', name: 'message' },
};

export type ConnectionArg = { label: string; type: 'string' | 'number' | 'bool' | 'scene' | 'clip' | 'animation' | 'field' | 'message' | 'sound' };

export interface ConnectionActionInfo {
  label: string;
  args?: ConnectionArg[];
  /** Only for the camera, or never for it. Default: objects only. */
  camera?: 'only' | 'also';
}

/** Built-in actions. Any other `do` is a method of a script on the target, or else of the target itself (a Phaser game object or the camera). */
export const CONNECTION_ACTIONS: Record<string, ConnectionActionInfo> = {
  show: { label: 'Show' },
  hide: { label: 'Hide' },
  toggle: { label: 'Show or hide' },
  destroy: { label: 'Destroy' },
  clip: { label: 'Play clip', args: [{ label: 'Clip', type: 'clip' }] },
  anim: { label: 'Play animation', args: [{ label: 'Animation', type: 'animation' }] },
  set: { label: 'Set a field', args: [{ label: 'Field', type: 'field' }, { label: 'Value', type: 'string' }] },
  scene: { label: 'Go to scene', args: [{ label: 'Scene', type: 'scene' }], camera: 'also' },
  send: { label: 'Send message', args: [{ label: 'Message', type: 'message' }], camera: 'also' },
  sound: { label: 'Play sound', args: [{ label: 'Sound', type: 'sound' }], camera: 'also' },
  music: { label: 'Play music', args: [{ label: 'Music', type: 'sound' }], camera: 'also' },
  stopsound: { label: 'Stop sound', args: [{ label: 'Sound', type: 'sound' }], camera: 'also' },
  shake: { label: 'Shake', args: [{ label: 'ms', type: 'number' }, { label: 'Strength', type: 'number' }], camera: 'only' },
  flash: { label: 'Flash', args: [{ label: 'ms', type: 'number' }], camera: 'only' },
  fade: { label: 'Fade out', args: [{ label: 'ms', type: 'number' }], camera: 'only' },
};

/** The event `game.events` carries a connection's Send message on: `(name, ...args)`. */
export const MESSAGE_EVENT = 'animo:message';

/** A connection's sentence, such as "entered by Player: Gate › Play clip open". */
export function describeConnection(c: Connection, nameOf: (id: string) => string | undefined = (id) => id): string {
  const ev = CONNECTION_EVENTS[c.on];
  const when = ev ? ev.label : c.on;
  const extra = ev?.who ? ` ${c.who?.length ? c.who.join(' or ') : 'anything'}` : c.name ? ` ${c.name}` : '';
  const target = !c.to ? 'itself' : c.to === CAMERA_TARGET ? 'Camera' : (nameOf(c.to) ?? c.to);
  const act = CONNECTION_ACTIONS[c.do]?.label ?? `${c.do}()`;
  const args = (c.args ?? []).map((a) => (typeof a === 'string' ? a : JSON.stringify(a))).join(' ');
  return `When ${when}${extra}: ${target} › ${act}${args ? ` ${args}` : ''}`;
}

/** The connections listed on an entry, skipping ones that aren't objects with `on` and `do`. */
export function readConnections(v: unknown): Connection[] {
  if (!Array.isArray(v)) return [];
  return v.filter((c): c is Connection => typeof c === 'object' && c !== null && typeof c.on === 'string' && typeof c.do === 'string');
}
